import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { KnownUser, KnownUserMapping, ResetPasswordEditor, UserOption } from '../../models/user-models';
import { Observable } from 'rxjs/Observable';
import { DbContext, DbTable } from '../data/dbcontext.service';
import * as crypto from 'crypto-js';
import { AppConfig } from '../../app/app.config';
import { CurrentDayService } from '../date/current-day.service';
import { MetadataService } from '../metadata/metadata-service.service';

@Injectable()
export class UserService {
  config = AppConfig.settings;

  private readonly knownUsersTable: DbTable;
  private readonly knownUsersMappingsTable: DbTable;

  private VERSION_NUMBER_KEY = 'version_number';

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private metadataService: MetadataService,
    private currentDayService: CurrentDayService
  ) {
    this.knownUsersTable = this.dbContext.tables.find(t => t.name === 'KnownUsers');
    this.knownUsersMappingsTable = this.dbContext.tables.find(t => t.name === 'KnownUserMappings');
  }

  resetPassword(editor: ResetPasswordEditor): Observable<boolean> {
    const url = `${this.config.apiUrl}api/security/users/update/password`;
    return this.http.post<boolean>(url, editor);
  }

  async saveKnownUser(knownUser: KnownUser) {
    await this.dbContext.addOrUpdate(this.knownUsersTable, knownUser.id, knownUser);
    const knownUsers = await this.dbContext.getAll(this.knownUsersTable);
    localStorage.setItem('known_users', JSON.stringify(knownUsers));
  }

  getKnownUserTempId(): Promise<number> {
    return this.dbContext.getTempId(this.knownUsersTable)
      .then(id => {
        return <number>id;
      });
  }

  saveKnownUserMapping(knownUserMapping: KnownUserMapping) {
    this.dbContext.addOrUpdate(this.knownUsersMappingsTable, knownUserMapping.username, knownUserMapping.userId);
  }

  async getKnownUsers(): Promise<KnownUser[]> {
    const inMemoryUsers = await this.dbContext.getAll(this.knownUsersTable);

    if (inMemoryUsers && inMemoryUsers.length > 0) {
      return inMemoryUsers;
    }

    let localStorageUsers = JSON.parse(localStorage.getItem('known_users'));

    if (!localStorageUsers) {
      localStorageUsers = [];
    }

    await this.saveUsers(localStorageUsers);

    return localStorageUsers;
  }

  async saveUsers(knownUsers: KnownUser[]) {
    for (let i = 0; i < knownUsers.length; i++) {
      await this.saveKnownUser(knownUsers[i]);
    }
  }

  async getKnownUser(id: number): Promise<KnownUser> {
    return this.dbContext.get(this.knownUsersTable, id);
  }

  async getKnownUserByUsername(username: string): Promise<KnownUser> {
    const knownUsers = await this.getKnownUsers();
    const userIndex = knownUsers.findIndex(e => e.username == username);

    if (userIndex != -1) {
      return knownUsers[userIndex];
    }
  }

  async getUserById(id: number) {
    const data = await this.getKnownUsers();

    if (!data) {
      return null;
    }

    return data.find(p => p.userId === id);
  }

  async getLocalUsers(): Promise<UserOption[]> {
    const data = await this.getKnownUsers();

    const options = [] as UserOption[];

    if (data) {
      data.forEach(user => {
        const option = new UserOption();
        option.id = user.id;
        option.name = user.username;
        options.push(option);
      });
    }

    return options;
  }

  async updateVersionNumber(): Promise<boolean> {
    const currentVersion = this.config.version;
    const savedVersion = localStorage.getItem(this.VERSION_NUMBER_KEY);

    if (!currentVersion) {
      return false;
    }

    if (!savedVersion || savedVersion !== currentVersion) {
      await this.dbContext.deleteAllDataForVersionUpdate();
      await this.metadataService.getMetadata();
      localStorage.clear();
      localStorage.setItem(this.VERSION_NUMBER_KEY, currentVersion);
      return true;
    }

    return false;
  }
}



// WEBPACK FOOTER //
// ./src/services/user/user.service.ts